import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import { LdsBadge } from "../../../components/lds/LdsBadge";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../../components/lds/LdsTable";
import { parseToDate } from "../../../utils/dateUtils";
import type { SchoolTask } from "../types";

export interface SchoolTaskTableProps {
  tasks: SchoolTask[];
  classroomMap: Map<string, string>;
  canManage: boolean;
  locale: string;
  onEdit: (task: SchoolTask) => void;
  onDelete: (task: SchoolTask) => Promise<void>;
}

export function SchoolTaskTable({
  tasks,
  classroomMap,
  canManage,
  locale,
  onEdit,
  onDelete,
}: SchoolTaskTableProps) {
  const { t } = useTranslation();
  const headCellClassName = "!border-b-0 !bg-slate-50 !p-3 !tracking-wide !text-inherit first:!rounded-none last:!rounded-none dark:!bg-slate-800";
  const cellClassName = "!p-3 !text-inherit";

  return (
    <LdsTableCard className="mt-4 !rounded-2xl !border-slate-200 !shadow-none dark:!border-slate-700">
      <LdsTableScroll className="rounded-2xl">
        <LdsTable className="min-w-[900px] text-sm">
          <LdsTableHead className="!bg-slate-50 !text-slate-500 dark:!bg-slate-800 dark:!text-slate-300 [&>tr]:!bg-slate-50 dark:[&>tr]:!bg-slate-800">
            <LdsTableRow className="!bg-slate-50 hover:!bg-slate-50 dark:!bg-slate-800 dark:hover:!bg-slate-800">
              <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.task", { defaultValue: "Task" })}</LdsTableHeadCell>
              <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.classrooms", { defaultValue: "Classrooms" })}</LdsTableHeadCell>
              <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.type", { defaultValue: "Type" })}</LdsTableHeadCell>
              <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.requirement", { defaultValue: "Requirement" })}</LdsTableHeadCell>
              <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.capacity", { defaultValue: "Capacity" })}</LdsTableHeadCell>
              <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.signups", { defaultValue: "Signups" })}</LdsTableHeadCell>
              <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.status", { defaultValue: "Status" })}</LdsTableHeadCell>
              <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.date", { defaultValue: "Date" })}</LdsTableHeadCell>
              {canManage && <LdsTableHeadCell className={headCellClassName}>{t("schoolTasks.actions", { defaultValue: "Actions" })}</LdsTableHeadCell>}
            </LdsTableRow>
          </LdsTableHead>
          <LdsTableBody className="[&>tr+tr>td]:!border-t-0">
            {tasks.map((task) => {
              const typeLabel = task.type === "PARENT_TASK" ? t("schoolTasks.types.parent", { defaultValue: "Parent task" }) : task.type === "GENERAL_TASK" ? t("schoolTasks.types.general", { defaultValue: "General task" }) : t("schoolTasks.types.holiday", { defaultValue: "Holiday cleaning" });
              const isFull = task.signupCount >= task.capacity;
              const taskDate = parseToDate(task.startsAt);
              return (
                <LdsTableRow key={task.id} onClick={() => canManage && onEdit(task)} className={`border-t border-slate-200 dark:border-slate-700 ${canManage ? "cursor-pointer hover:!bg-slate-50 dark:hover:!bg-slate-800/60" : "hover:!bg-white dark:hover:!bg-slate-900"}`}>
                  <LdsTableCell className={`${cellClassName} font-bold`}><span>{task.name}</span>{task.activity && <span className="mt-1 block w-fit rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-bold text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200">{task.activity.name}</span>}</LdsTableCell>
                  <LdsTableCell className={cellClassName}>{task.classroomIds?.length ? task.classroomIds.map((id) => classroomMap.get(id) || id).join(", ") : t("schoolTasks.schoolWide", { defaultValue: "School-wide" })}</LdsTableCell>
                  <LdsTableCell className={cellClassName}>{typeLabel}</LdsTableCell>
                  <LdsTableCell className={cellClassName}>{task.optional ? t("schoolTasks.optional", { defaultValue: "Optional" }) : t("schoolTasks.required", { defaultValue: "Required" })}</LdsTableCell>
                  <LdsTableCell className={cellClassName}>{task.capacity}</LdsTableCell>
                  <LdsTableCell className={cellClassName}>{task.signupCount}</LdsTableCell>
                  <LdsTableCell className={cellClassName}><LdsBadge tone={isFull ? "neutral" : "success"} className="px-2.5">{isFull ? t("schoolTasks.full", { defaultValue: "Full" }) : t("schoolTasks.available", { defaultValue: "Available" })}</LdsBadge></LdsTableCell>
                  <LdsTableCell className={cellClassName}>{taskDate ? taskDate.toLocaleDateString(locale) : "—"}</LdsTableCell>
                  {canManage && <LdsTableCell className={cellClassName}><div className="flex items-center gap-1"><button type="button" aria-label={t("schoolTasks.deleteTaskLabel", { defaultValue: "Delete task {{name}}", name: task.name })} onClick={(event) => { event.stopPropagation(); void onDelete(task); }} className="rounded-full p-3 text-red-600"><FaTrash /></button></div></LdsTableCell>}
                </LdsTableRow>
              );
            })}
            {tasks.length === 0 && <LdsTableRow className="hover:!bg-white dark:hover:!bg-slate-900"><LdsTableCell colSpan={canManage ? 9 : 8} className="!p-8 text-center !text-slate-500">{t("schoolTasks.noTasksMatch", { defaultValue: "No tasks match the selected filters." })}</LdsTableCell></LdsTableRow>}
          </LdsTableBody>
        </LdsTable>
      </LdsTableScroll>
    </LdsTableCard>
  );
}
